/* eslint-disable @next/next/no-img-element */
import { asText, Content, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { crush } from '@tedconf/js-crushinator-helpers';
import cx from 'classnames';
import { PageLayoutContext } from 'components/pages/prismic/Page/PageContent';
import PrismicConditionalLink from 'components/PRISMIC/PrismicConditionalLink';
import PrismicInlineText from 'components/PRISMIC/PrismicInlineText';
import { PrismicNextImage } from 'components/PRISMIC/PrismicNextImage';
import PrismicStyledText from 'components/PRISMIC/PrismicStyledText';
import styles from 'components/PRISMIC/scss/pages.module.scss';
import { PageBuilderHeading } from 'components/PRISMIC/slices/Heading/Heading';
import { formatAsTimestamp } from 'lib/util';
import { useContext, type JSX } from 'react';

/**
 * Props for `Gallery`.
 */
export type GalleryProps = SliceComponentProps<Content.GallerySlice>;

/**
 * Component for "Gallery" Slices.
 */
const Gallery = ({ slice }: GalleryProps): JSX.Element => {
  const columnWidth: number = useContext(PageLayoutContext);

  switch (slice.variation) {
    case 'default':
      return (
        <div
          data-slice-type={slice.slice_type}
          className={styles['pages-module']}
        >
          <PageBuilderHeading level="default">
            <PrismicRichText field={slice.primary.heading} />
          </PageBuilderHeading>
          <div
            className={cx(
              styles.row,
              columnWidth > 0.7 && styles['row-sm-2up']
            )}
          >
            {slice.items.map(item => (
              <div className={styles.col}>
                <div
                  className={cx(
                    styles.media,
                    styles['row--equal-height'],
                    styles.m3
                  )}
                >
                  {isFilled.image(item.thumbnail) && (
                    <div className={styles.media__image}>
                      <PrismicConditionalLink field={item.link}>
                        <PrismicNextImage
                          className={styles.image}
                          field={item.thumbnail}
                          width={item.thumbnail.dimensions.width}
                          height={item.thumbnail.dimensions.height}
                        />
                      </PrismicConditionalLink>
                    </div>
                  )}
                  {item.legacyThumbnailUrl && (
                    <div>
                      <img
                        className={cx(
                          styles['pages-button'],
                          styles['thumb--medallions']
                        )}
                        src={crush(item.legacyThumbnailUrl || '')}
                        alt={item.legacyThumbnailAlt || ''}
                      />
                    </div>
                  )}
                  <div
                    className={cx(
                      styles.media__message,
                      styles.p4,
                      styles.flex1
                    )}
                  >
                    <PrismicStyledText field={item.text} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'people':
      return (
        <div
          data-slice-type={slice.slice_type}
          className={styles['pages-module']}
        >
          <PageBuilderHeading level="default">
            <PrismicRichText field={slice.primary.heading} />
          </PageBuilderHeading>
          <div
            className={cx(
              styles.row,
              columnWidth === 1
                ? styles['row-lg-6up'] + ' ' + styles['row-sm-4up']
                : columnWidth > 0.7
                  ? styles['row-sm-5up']
                  : columnWidth > 0.5 && styles['row-sm-3up']
            )}
          >
            {slice.items.map(item => {
              return (
                <div className={styles.col}>
                  <div
                    className={cx(
                      styles.media,
                      styles.m4,
                      styles['media--sm-v']
                    )}
                  >
                    {isFilled.image(item.thumbnail) && (
                      <div
                        className={cx(
                          styles.media__image,
                          styles['media__image--thumb']
                        )}
                      >
                        <PrismicConditionalLink field={item.link}>
                          <PrismicNextImage
                            className={styles.image}
                            field={item.thumbnail}
                            width={item.thumbnail.dimensions.width}
                            height={item.thumbnail.dimensions.height}
                          />
                        </PrismicConditionalLink>
                      </div>
                    )}
                    {item.legacyThumbnailUrl && (
                      <img
                        className={cx(styles.m4)}
                        src={crush(item.legacyThumbnailUrl || '')}
                        style={{
                          aspectRatio: '1 / 1',
                          objectFit: 'cover',
                          objectPosition: '0 0'
                        }}
                        alt={item.legacyThumbnailAlt || ''}
                      />
                    )}
                    <div className={cx(styles.media__message)}>
                      <h4 className={cx(styles.h7, styles.m5)}>
                        <PrismicConditionalLink field={item.link}>
                          {item.name || 'Name'}
                        </PrismicConditionalLink>
                      </h4>
                      <p className={styles.p4}>
                        <PrismicStyledText field={item.title} />
                        <strong>
                          <PrismicStyledText field={item.locations} />
                        </strong>
                        <PrismicStyledText field={item.events} />
                        <PrismicStyledText field={item.languages} />
                        <em>
                          <PrismicStyledText field={item.flair} />
                        </em>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    case 'videos':
    default:
      return (
        <div
          data-slice-type={slice.slice_type}
          className={cx(styles['pages-module'], styles['pages-video-gallery'])}
        >
          <PageBuilderHeading level="default">
            <PrismicRichText field={slice.primary.heading} />
          </PageBuilderHeading>
          <div
            className={cx(
              styles.row,
              columnWidth === 1
                ? styles['row-lg-6up'] + ' ' + styles['row-sm-4up']
                : columnWidth > 0.7
                  ? styles['row-sm-4up']
                  : columnWidth > 0.5 && styles['row-sm-3up']
            )}
          >
            {slice.items.map(item => (
              <div key={asText(item.title) || ''} className={styles.col}>
                <div className={styles['pages-video-gallery__item']}>
                  <div
                    className={cx(
                      styles.media,
                      styles.m3,
                      styles['media--sm-v']
                    )}
                  >
                    <div
                      className={cx(
                        styles.media__image,
                        styles['media__image--thumb']
                      )}
                    >
                      <PrismicConditionalLink field={item.link}>
                        <div
                          className={cx(
                            styles.thumb,
                            styles['thumb--video'],
                            styles['thumb--crop-top']
                          )}
                        >
                          <span className={styles.thumb__sizer}>
                            <span className={styles.thumb__tugger}>
                              {isFilled.image(item.thumbnail) ? (
                                <PrismicNextImage
                                  className={styles.thumb__image}
                                  field={item.thumbnail}
                                  width={item.thumbnail.dimensions.width}
                                  height={item.thumbnail.dimensions.height}
                                />
                              ) : (
                                <img
                                  className={styles.thumb__image}
                                  src={crush(item.legacyThumbnailUrl || '', {
                                    width: 151
                                  })}
                                  alt={item.legacyThumbnailAlt || ''}
                                />
                              )}
                            </span>
                          </span>
                          {item.duration && (
                            <span className={styles.thumb__duration}>
                              {formatAsTimestamp(parseInt(item.duration))}
                            </span>
                          )}
                        </div>
                      </PrismicConditionalLink>
                    </div>
                    <div className={styles.media__message}>
                      <div className={styles.e1}>
                        <PrismicInlineText field={item.kicker} />
                      </div>
                      <h4 className={styles.h9}>
                        <PrismicConditionalLink field={item.link}>
                          <PrismicInlineText field={item.title} />
                        </PrismicConditionalLink>
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
  }
};

export default Gallery;
